import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Play, ClipboardList, CalendarPlus, UserCheck, Repeat } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { api, web, errMsg, toList } from "../../lib/api";
import { PageHeader, Loader, ErrorNote, Empty, Btn, shortDate } from "../../components/app/kit";
import { Modal } from "../../components/app/Form";
import { EventForm } from "../../components/app/EventForm";
import { CalendarLink } from "../../components/app/CalendarLink";
import { eventsOf, eventTitle, eventDate } from "./Overview";
import { RemindTeam, canRemind } from "../../components/app/RemindTeam";

const timeOf = (s) => {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) || !String(s).includes("T") ? "" : d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

const DeleteSeries = ({ target, tid, onClose, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const run = async (ids) => {
    setBusy(true);
    try {
      const { data } = await web.post(`/teams/${tid}/schedule/series/${target.series.id}/delete`, { event_ids: ids });
      toast.success(`${data?.deleted ?? ids.length} event${ids.length === 1 ? "" : "s"} deleted${data?.failed ? `, ${data.failed} failed` : ""}`);
      onDeleted();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const e = target?.event;
  const later = e ? target.series.events.filter((x) => new Date(x.start) >= new Date(e.start)).map((x) => x.id) : [];
  return (
    <Modal open={!!target} onClose={onClose} testid="delete-series-dialog" title="Delete repeating event" description={e ? `"${eventTitle(e)}" on ${shortDate(eventDate(e))} is part of a series.` : ""}>
      <div className="mt-4 flex flex-col gap-2">
        <Btn variant="danger" disabled={busy} data-testid="delete-series-one" onClick={() => run([e.id])}>This event only</Btn>
        <Btn variant="danger" disabled={busy} data-testid="delete-series-later" onClick={() => run(later)}>This and later events ({later.length})</Btn>
        <Btn variant="ghost" disabled={busy} data-testid="delete-series-cancel" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
};

const EventActions = ({ e, game, tid, series, canEdit, levelName, lastReminder, onReminded, onEdit, onDeleted, onDeleteSeries }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const start = async () => {
    if (game) return navigate(`/coach/games/${game.id}/stats`);
    setBusy(true);
    try {
      const { data } = await api.post(`/teams/${tid}/schedule/${e.id}/start-game`);
      navigate(`/coach/games/${data?.id ?? data?.game?.id}/stats`);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };
  const remove = async () => {
    if (series) return onDeleteSeries({ event: e, series });
    if (!window.confirm(`Delete "${eventTitle(e)}" from the schedule?`)) return;
    try {
      await api.delete(`/teams/${tid}/schedule/${e.id}`);
      toast.success("Event deleted");
      onDeleted();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="mt-3 flex flex-wrap gap-2 border-t border-[#1E293B] pt-3">
      {canEdit && e.type === "game" && (
        <Btn data-testid={game ? `event-enter-stats-${e.id}` : `event-start-game-${e.id}`} disabled={busy} onClick={start} className="px-4 py-2 text-xs">
          {game ? <><ClipboardList className="h-3.5 w-3.5" />Enter stats</> : <><Play className="h-3.5 w-3.5" />{busy ? "Starting…" : "Start game"}</>}
        </Btn>
      )}
      {canRemind(eventDate(e)) && <RemindTeam e={e} tid={tid} levelName={levelName} last={lastReminder} title={eventTitle(e)} onSent={onReminded} />}
      <Btn variant="ghost" data-testid={`event-attendance-${e.id}`} onClick={() => navigate(`/coach/attendance?event=${e.id}`)} className="px-4 py-2 text-xs"><UserCheck className="h-3.5 w-3.5" />Attendance</Btn>
      <Btn variant="ghost" data-testid={`event-edit-${e.id}`} onClick={() => onEdit(e)} className="px-4 py-2 text-xs"><Pencil className="h-3.5 w-3.5" />Edit</Btn>
      <Btn variant="danger" data-testid={`event-delete-${e.id}`} onClick={remove} className="px-4 py-2 text-xs"><Trash2 className="h-3.5 w-3.5" />Delete</Btn>
    </div>
  );
};

const LEVELS = [["varsity", "Varsity"], ["jv", "JV"]];

const LevelTabs = ({ value, onChange, count }) => (
  <div className="mb-6 inline-flex rounded-full border border-[#1E293B] bg-[#0F172A] p-1" role="tablist">
    {LEVELS.map(([k, l]) => (
      <button key={k} role="tab" aria-selected={value === k} data-testid={`schedule-tab-${k}`} onClick={() => onChange(k)}
        className={`rounded-full px-5 py-2 font-mono-gd text-xs uppercase tracking-wider transition-colors ${value === k ? "bg-[#39FF14] text-[#0A0E1A]" : "text-slate-300 hover:text-white"}`}>
        {l} <span className="opacity-70">{count(k)}</span>
      </button>
    ))}
  </div>
);

export function Schedule() {
  const { membership, canEdit, canRun, level } = useAuth();
  const tid = membership?.team_id;
  const sched = useApi(tid && `/teams/${tid}/schedule`);
  const games = useApi(tid && canEdit && `/teams/${tid}/games`);
  const seriesMap = useApi(tid && canRun && `/teams/${tid}/schedule/series`, web);
  const levelMap = useApi(tid && `/teams/${tid}/schedule/levels`, web);
  const reminders = useApi(tid && canRun && `/teams/${tid}/schedule/reminders`, web);
  const [tab, setTab] = useState(level === "jv" ? "jv" : "varsity");
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);
  const [cal, setCal] = useState(false);
  const gameFor = (e) => toList(games.data, ["games"]).find((g) => g.schedule_id === e.id);
  const lmap = levelMap.data && typeof levelMap.data === "object" ? levelMap.data : {};
  const levelOfEvent = (e) => (lmap[e?.id] === "jv" ? "jv" : "varsity");
  const allEvents = eventsOf(sched.data).sort((a, b) => new Date(eventDate(a)) - new Date(eventDate(b)));
  const events = allEvents.filter((e) => levelOfEvent(e) === tab);
  const smap = seriesMap.data && typeof seriesMap.data === "object" ? seriesMap.data : {};
  const seriesOf = (e) => (smap[e?.id] ? { id: smap[e.id], events: allEvents.filter((x) => smap[x.id] === smap[e.id]) } : null);
  const reload = () => { sched.reload(); seriesMap.reload(); levelMap.reload(); };
  const now = Date.now();
  const tabName = tab === "jv" ? "JV" : "Varsity";
  return (
    <div>
      <PageHeader eyebrow={membership?.team_name} title="Schedule">
        {canEdit && <Btn variant="ghost" data-testid="calendar-link-button" onClick={() => setCal(true)}><CalendarPlus className="h-4 w-4" />{tabName} calendar link</Btn>}
        {canRun && <Btn data-testid="add-event-button" onClick={() => setForm({})}><Plus className="h-4 w-4" />Add {tabName} event</Btn>}
      </PageHeader>
      {canEdit && <CalendarLink open={cal} onClose={() => setCal(false)} teamId={tid} teamName={membership?.team_name} path={`/teams/${tid}/calendar-link?level=${tab}`} title={`${tabName} calendar`}
        description={`${tabName} players and parents add ${membership?.team_name ?? "your team"}'s ${tabName} games and practices to their phone calendar. Only events on the ${tabName} schedule are included.`}
        copied={`${tabName} link copied. Share it with ${tabName} players and parents`} />}
      {canRun && <EventForm open={!!form} onClose={() => setForm(null)} teamId={tid} event={form?.id ? form : null} series={form?.id ? seriesOf(form) : null} level={form?.id ? levelOfEvent(form) : tab} onSaved={reload} />}
      {canRun && <DeleteSeries target={del} tid={tid} onClose={() => setDel(null)} onDeleted={reload} />}
      <LevelTabs value={tab} onChange={setTab} count={(k) => allEvents.filter((e) => levelOfEvent(e) === k).length} />
      {sched.loading ? <Loader /> : sched.error ? <ErrorNote error={sched.error} /> : !events.length ? <Empty title={`No ${tabName} events`} text={canRun ? `Add ${tabName} games and practices so your team sees them.` : undefined} /> : (
        <ol className="relative space-y-3 border-l border-[#1E293B] pl-6">
          {events.map((e, i) => {
            const past = new Date(eventDate(e)).getTime() < now - 864e5;
            return (
              <li key={e.id ?? i} data-testid="schedule-event" className={`relative rounded-2xl border border-[#1E293B] bg-[#0F172A] px-5 py-4 ${past ? "opacity-70" : ""}`}>
                <span className={`absolute -left-[31px] top-6 h-3 w-3 rounded-full ${past ? "bg-slate-600" : "bg-[#39FF14] shadow-[0_0_12px_#39FF14]"}`} />
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-display text-xl font-bold uppercase text-white">{eventTitle(e)}</p>
                  <p className="flex items-center gap-2 font-mono-gd text-xs uppercase tracking-wider text-[#39FF14]">{smap[e.id] && <span data-testid={`event-repeats-${e.id}`} className="inline-flex items-center gap-1 text-slate-300"><Repeat className="h-3 w-3" />Repeats</span>}{e.type ?? e.kind ?? (past ? "Past" : "Upcoming")}</p>
                </div>
                <p className="font-mono-gd text-xs uppercase tracking-wider text-slate-400">{shortDate(eventDate(e))}{timeOf(eventDate(e)) ? ` · ${timeOf(eventDate(e))}` : e.time ? ` · ${e.time}` : ""}{e.location ? ` · ${e.location}` : ""}</p>
                {e.notes && <p className="mt-2 text-sm text-slate-300">{e.notes}</p>}
                {canRun && e.id && <EventActions e={e} game={gameFor(e)} tid={tid} series={seriesOf(e)} canEdit={canEdit} levelName={tabName} lastReminder={reminders.data?.[e.id]} onReminded={reminders.reload} onEdit={setForm} onDeleted={reload} onDeleteSeries={setDel} />}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
